import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";

/**
 * The set-entry control, and the loudest thing on the screen.
 *
 * Between two series a load moves by one plate, not by an arbitrary number, so
 * the +/- buttons are the primary input and the keyboard is the fallback: tap
 * the number to type it. Buttons are 64px and repeat when held, because the
 * hands doing this are shaking.
 */

interface StepperProps {
  label: string;
  unit?: string;
  value: number;
  step: number;
  min?: number;
  max?: number;
  decimals?: number;
  onChange: (value: number) => void;
  /** The poster size, for the one figure the screen is about. */
  hero?: boolean;
  /** Replaces the label line under the figure. */
  caption?: ReactNode;
  /** Half width, two to a row: the reps before and after a drop. */
  compact?: boolean;
}

export function Stepper({
  label,
  unit,
  value,
  step,
  min = 0,
  max = 999,
  decimals = 1,
  onChange,
  hero = false,
  caption,
  compact = false,
}: StepperProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const repeatTimer = useRef<number | null>(null);
  const repeatDelay = useRef<number | null>(null);
  // The repeat timer needs the value as it is now, not the one captured when
  // the press started.
  const latest = useRef(value);
  useEffect(() => {
    latest.current = value;
  }, [value]);

  const clamp = (next: number) => Math.min(max, Math.max(min, Math.round(next * 100) / 100));

  const apply = (delta: number) => {
    const next = clamp(latest.current + delta);
    latest.current = next;
    onChange(next);
  };

  /** Hold to repeat: 450ms before the first repeat, then every 90ms. */
  const startRepeat = (delta: number) => {
    apply(delta);
    repeatDelay.current = window.setTimeout(() => {
      repeatTimer.current = window.setInterval(() => apply(delta), 90);
    }, 450);
  };

  const stopRepeat = () => {
    if (repeatDelay.current) window.clearTimeout(repeatDelay.current);
    if (repeatTimer.current) window.clearInterval(repeatTimer.current);
    repeatDelay.current = null;
    repeatTimer.current = null;
  };

  const commitDraft = () => {
    const parsed = Number.parseFloat(draft.replace(",", "."));
    if (Number.isFinite(parsed)) onChange(clamp(parsed));
    setEditing(false);
  };

  const formatted = decimals > 0 ? value.toFixed(decimals).replace(".", ",").replace(/,0$/, "") : String(value);
  // The poster figure is sized to its well, by what it has to show: at one
  // size for all, "13,6 kg" ran over the + button (12/10). The wide digits
  // take about 0.8em each, the comma half that; the rest is the unit's room.
  const ems = [...formatted].reduce((sum, char) => sum + (char === "," ? 0.4 : 0.8), 0);
  const heroSize = hero ? { fontSize: `min(6.5rem, 19vw, calc((100cqi - 3.4rem) / ${ems}))` } : undefined;
  const figure = hero
    ? "display font-bold"
    : compact
      ? "num text-[2rem] leading-none font-bold"
      : "num text-[2.6rem] leading-none font-bold";

  return (
    <div className={["flex items-stretch", compact ? "min-w-0 gap-1" : "gap-2"].join(" ")}>
      <StepButton
        onPress={() => startRepeat(-step)}
        onRelease={stopRepeat}
        label={`Retirer ${step}`}
        size={hero ? "hero" : compact ? "compact" : "normal"}
      >
        −
      </StepButton>

      <button
        type="button"
        onClick={() => {
          // With the comma it is read with — typed back, comma or point is the
          // same — and unrounded: 11,25 must not come back as 11,3.
          setDraft(String(value).replace(".", ","));
          setEditing(true);
        }}
        className={[
          "flex min-w-0 flex-1 flex-col items-center justify-center rounded-card",
          hero ? "card @container py-4" : compact ? "bg-surface-high py-1.5" : "bg-surface-high py-2",
        ].join(" ")}
      >
        {editing ? (
          <input
            autoFocus
            // decimal keypad, not the full keyboard: fewer keys, bigger targets
            inputMode="decimal"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onBlur={commitDraft}
            onKeyDown={(event) => event.key === "Enter" && commitDraft()}
            className={["w-full bg-transparent text-center text-ink outline-none", figure].join(" ")}
            style={heroSize}
          />
        ) : hero ? (
          <span className="flex items-baseline">
            <span className={figure} style={heroSize}>
              {formatted}
            </span>
            {unit && <span className="wide ml-1.5 text-[1.3rem] font-bold text-ink-mute">{unit}</span>}
          </span>
        ) : (
          <span className={figure}>{formatted}</span>
        )}
        {caption ?? (
          <span className={["mt-1 text-ink-mute", compact ? "text-[0.7rem]" : "text-[0.76rem]"].join(" ")}>
            {label}
            {unit ? ` · ${unit}` : ""}
          </span>
        )}
      </button>

      <StepButton
        onPress={() => startRepeat(step)}
        onRelease={stopRepeat}
        label={`Ajouter ${step}`}
        size={hero ? "hero" : compact ? "compact" : "normal"}
      >
        +
      </StepButton>
    </div>
  );
}

function StepButton({
  children,
  onPress,
  onRelease,
  label,
  size,
}: {
  children: ReactNode;
  onPress: () => void;
  onRelease: () => void;
  label: string;
  size: "hero" | "normal" | "compact";
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onPointerDown={(event) => {
        event.preventDefault();
        onPress();
      }}
      onPointerUp={onRelease}
      onPointerLeave={onRelease}
      onPointerCancel={onRelease}
      className={[
        "num shrink-0 rounded-card bg-surface-high font-bold text-ink-soft active:bg-accent active:text-accent-ink",
        // 44px still, for the compact one: Apple's floor for a thumb.
        size === "hero" ? "w-[4.5rem] text-[2.2rem]" : size === "compact" ? "w-11 text-[1.6rem]" : "w-16 text-[1.9rem]",
      ].join(" ")}
    >
      {children}
    </button>
  );
}
